import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Grid,
  Button,
  Flex,
  VStack,
  Select,
  Input,
  useColorModeValue,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  Container,
  FormControl,
  FormLabel,
  HStack,
  GridItem,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  IconButton,
} from "@chakra-ui/react";
import { ChevronRightIcon, ChevronLeftIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { FaPlusCircle, FaSearch, FaUndo } from "react-icons/fa";
import {
  collection,
  onSnapshot,
  query,
  where,
  firestore,
} from "../../firebase/firebaseConfig";
import ListingCard from "../../components/ListingCard";
import ListingDetailsModal from "../../components/ListingDetailsModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getFlagByCountryCode } from "../../utils/useFlags";
import countryCodes from "../../components/data/CountryCodes.json";
import { useTranslation } from "react-i18next";


const ListingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const textColor = useColorModeValue("brand.200", "white");
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const bgColor = useColorModeValue("brand.200", "blue.300");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedListing, setSelectedListing] = useState(null);
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const listingsPerPage = 5;

  // Filters (Initially Empty)
  const [rate, setRate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  // Unique Filtered Options

  const [uniqueCurrencies, setUniqueCurrencies] = useState([]);
  const [uniquePaymentMethods, setUniquePaymentMethods] = useState([]);
  const [uniqueExchangeRatePairs, setUniqueExchangeRatePairs] = useState([]);
  const [selectedExchangeRatePair, setSelectedExchangeRatePair] = useState("");
  const [selectedDesiredCountry, setSelectedDesiredCountry] = useState(""); // ✅ Fix: Declare the state
  const [uniqueDesiredCountries, setUniqueDesiredCountries] = useState([]); // ✅ Fix: Declare unique desired countries

  // Check if URL contains a listing ID and open modal
  useEffect(() => {
    const listingId = searchParams.get("listing");
    if (listingId) {
      const matchedListing = listings.find((l) => l.id === listingId);
      if (matchedListing) {
        setSelectedListing(matchedListing);
        onOpen();
      }
    }
  }, [searchParams, listings, onOpen]);

  useEffect(() => {
    const fetchListings = () => {
      try {
        const listingsRef = collection(firestore, "listings");
        const listingsQuery = query(
          listingsRef,
          where("status", "in", ["active", "matched"])
        );

        const unsubscribe = onSnapshot(listingsQuery, (snapshot) => {
          const listingsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // ✅ Sort by createdAt DESCENDING (most recent first)
          listingsData.sort((a, b) => {
            const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return bDate - aDate; // Descending
          });

          // Update your listings state immediately
          setListings(listingsData);
          setFilteredListings(listingsData);

          // ✅ Extract unique and sorted values for dropdowns
          const desiredCountries = [...new Set(listingsData.map((listing) => listing.desiredCountry))].sort();
          const currencies = [...new Set(listingsData.map((listing) => listing.offeredCurrency))].sort();
          const paymentMethods = [...new Set(listingsData.flatMap((listing) => listing.paymentMethods || []))].sort();

          // ✅ Sorted exchangeRatePairs
          const exchangeRatePairs = [...new Set(listingsData.map((listing) => listing.exchangeRatePair))].sort();

          // ✅ Update states with sorted lists
          setUniqueDesiredCountries(desiredCountries);
          setUniqueCurrencies(currencies);
          setUniquePaymentMethods(paymentMethods);
          setUniqueExchangeRatePairs(exchangeRatePairs);

          // Finish loading
          setLoading(false);
        });

        return unsubscribe; // cleanup
      } catch (err) {
        console.error(t("listingpage.fetch_error"), err);
        setError(t("listingpage.fetch_error_message"));
        setLoading(false);
      }
    };

    const unsubscribe = fetchListings();
    return () => unsubscribe();
  }, []);



  // Function to apply all filters
  const handleFilterListings = () => {
    let filtered = listings;

    if (selectedDesiredCountry) {
      filtered = filtered.filter((listing) => listing.desiredCountry === selectedDesiredCountry);
    }

    if (rate && !isNaN(rate)) {
      filtered = filtered.filter((listing) => Number(listing.rate) >= Number(rate));
    }

    if (paymentMethod) {
      filtered = filtered.filter(
        (listing) => listing.paymentMethods && listing.paymentMethods.includes(paymentMethod)
      );
    }

    if (selectedExchangeRatePair) {
      filtered = filtered.filter((listing) => listing.exchangeRatePair === selectedExchangeRatePair);
    }

    setFilteredListings(filtered);
    setCurrentPage(1);
  };

  // Reset Filters
  const handleResetFilters = () => {
    setSelectedDesiredCountry("");
    setRate("");
    setPaymentMethod("");
    setSelectedExchangeRatePair("");
    setFilteredListings(listings);
    setCurrentPage(1);
  };

  // Call the filtering function inside useEffect
  useEffect(() => {
    handleFilterListings();
  }, [selectedDesiredCountry, rate, paymentMethod, selectedExchangeRatePair, listings]);




  // Open listing details modal
  const openListingDetails = (listing) => {
    setSelectedListing(listing);
    setSearchParams({ listing: listing.id }); // ✅ Update URL with listing ID
    onOpen();
  };

  // Close modal and clear listing ID from URL
  const closeListingDetails = () => {
    setSearchParams({}); // ✅ Remove listing ID from URL
    onClose();
  };


  // Calculate the listings to display on the current page
  const indexOfLastListing = currentPage * listingsPerPage;
  const indexOfFirstListing = indexOfLastListing - listingsPerPage;
  const currentListings = filteredListings.slice(indexOfFirstListing, indexOfLastListing);


  // Change Page
  const nextPage = () => {
    if (currentPage < Math.ceil(filteredListings.length / listingsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };


  return (
    <Box bg="brand.200" color="brand.500">
      {/* Hero Section */}
      <Box
        py={12}
        px={8}
        textAlign="center"
        // bgGradient="linear(to-r, blue.500, blue.400)"
        color="brand.300"
        borderRadius="lg"
      >
        <Heading
          as={motion.h1}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          size="2xl"
          fontWeight="bold"
          mb={4}
          color="brand.300"
        // textShadow="0px 2px 5px rgba(0, 0, 0, 0.3)"
        >
          {t("listingpage.hero_title")}
        </Heading>
        <Text
          as={motion.p}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          fontSize="xl" 
          mb={6}
          color="brand.400"
        // textShadow="0px 1px 3px rgba(0, 0, 0, 0.2)"
        >
          {t("listingpage.hero_description")}
        </Text>

        <Flex justify="center" align="center" gap={4}>
          {/* Highlighted Post Listing Button */}
          <Button
            as={motion.button}
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            leftIcon={<FaPlusCircle />}
            bg="brand.300"
            color="white"
            fontWeight="bold"
            _hover={{ bg: "brand.100", color: "brand.300", transform: "scale(1.05)" }}
            size="lg"
            shadow="lg"
            transition="0.3s"
            onClick={() => navigate("/personal/post-listing")} // Redirect to post listing page
          >
            {t("listingpage.post_listing_button")}
          </Button>

        </Flex>
      </Box>



      {/* Filters Section */}
      <Box py={6} px={4} bg="brand.100" borderRadius="2xl">
        <Container maxW="container.xl">
          <VStack spacing={6} align="stretch">
            <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(4, 1fr)" }} gap={6}>

              {/* Country Filter */}
              <GridItem>
                <FormControl>
                  <FormLabel
                    id="desiredCountry"
                    pb={{ base: '1', md: '0' }}
                    mb={{ base: '2', md: '1' }}
                    color="brand.300"
                    fontSize="xl"
                    fontWeight={{ base: 'normal', md: 'bold' }}
                  >
                    {t("listingpage.sending_money_label")}
                  </FormLabel>

                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      bg="white"
                      color="brand.500"
                      fontSize="md"
                      fontWeight="bold"
                      w="full"
                      size="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      rounded="md"
                    >
                      {selectedDesiredCountry || t(" ")}
                    </MenuButton>

                    <MenuList>
                      <MenuItem key="all" onClick={() => setSelectedDesiredCountry("")} />
                      {uniqueDesiredCountries.map((country) => (
                        <MenuItem key={country} value={country} onClick={() => setSelectedDesiredCountry(country)}>
                          {country}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </FormControl>
              </GridItem>

              {/* Rate Filter */}
              <GridItem>
                <FormControl id="rate">
                  <FormLabel
                    pb={1}
                    color="brand.300"
                    mb={isSmallScreen ? 2 : 0}
                    fontSize="xl"
                    fontWeight={{ base: 'normal', md: 'bold' }}
                  >
                    {t("listingpage.rate_label")}
                  </FormLabel>
                  <Grid templateColumns="repeat(4, 1fr)" gap={2}>
                    {/* Rate Input */}
                    <GridItem colSpan={2}>
                      <Input bg="white" type="number" value={rate} onChange={(e) => setRate(e.target.value)} />
                    </GridItem>

                    {/* Exchange Rate Pair Dropdown */}
                    <GridItem colSpan={2}>
                      <Menu>
                        <MenuButton
                          as={Button}
                          rightIcon={<ChevronDownIcon />}
                          bg="white"
                          color="brand.500"
                          fontSize="md"
                          fontWeight="bold"
                          w="full"
                          size="md"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          rounded="md"
                        >
                          {selectedExchangeRatePair || t(" ")}
                        </MenuButton>

                        <MenuList>
                          <MenuItem key="all" onClick={() => setSelectedExchangeRatePair("")} />
                          {uniqueExchangeRatePairs.map((pair) => (
                            <MenuItem key={pair} value={pair} onClick={() => setSelectedExchangeRatePair(pair)}>
                              {pair}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    </GridItem>
                  </Grid>
                </FormControl>
              </GridItem>

              {/* Payment Method Field */}
              <GridItem>
                <FormControl>
                  <FormLabel
                    pb={1}
                    color="brand.300"
                    fontSize="xl"
                    fontWeight={{ base: 'normal', md: 'bold' }}
                    mb={isSmallScreen ? 2 : 0}
                  >
                    {t("listingpage.paying_with_label")}
                  </FormLabel>
                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      bg="white"
                      color="brand.500"
                      fontSize="md"
                      fontWeight="bold"
                      w="full"
                      size="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      rounded="md"
                    >
                      {paymentMethod || t(" ")}
                    </MenuButton>

                    <MenuList>
                      <MenuItem key="all" onClick={() => setPaymentMethod("")} />
                      {uniquePaymentMethods.map((method) => (
                        <MenuItem key={method} value={method} onClick={() => setPaymentMethod(method)}>
                          {method}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </FormControl>
              </GridItem>


              {/* Action Buttons */}
              <GridItem justifySelf="center" alignSelf="end">
                <Flex gap={2}>
                  <Button
                    leftIcon={<FaSearch />}
                    color="white"
                    bg="brand.300"
                    onClick={handleFilterListings}
                  >
                    {t("listingpage.find_peer_button")}
                  </Button>
                  <Button
                    leftIcon={<FaUndo />}
                    color="white"
                    bg="gray.500"
                    onClick={handleResetFilters}
                  >
                    {/* {t("listingpage.reset_button")} */}
                  </Button>
                </Flex>
              </GridItem>


            </Grid>
          </VStack>
        </Container>
      </Box>


      {/* Listings Section */}
      <Box py={8} px={4}>
        <Container maxW="container.xl"> {/* Container for Listings */}
          {/* <Heading textAlign="center" mb={10} color={textColor}>
            Active Listings
          </Heading> */}
          {loading ? (
            <Flex justify="center" mt={6}>
              <Spinner size="xl" />
            </Flex>
          ) : error ? (
            <Alert status="error" borderRadius="md" maxW="600px" mx="auto" mt={4}>
              <AlertIcon />
              {error}
            </Alert>
          ) : filteredListings.length === 0 ? (
            <Text color={textColor} textAlign="center">
              {t("listingpage.no_listings_message")}
            </Text>
          ) : (
            <>
              <Grid // Use Grid instead of SimpleGrid
                templateColumns={{ base: "1fr", md: "repeat(1, 1fr)" }} // 1 column on small, 2 on medium and larger
                gap={8}
              >
                {currentListings.map((listing) => (
                  <motion.div
                    key={listing.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <ListingCard listing={listing} onOpenDetails={openListingDetails} />
                  </motion.div>
                ))}
              </Grid>

              {/* Pagination Controls */}
              <Flex justify="center" align="center" mt={8} gap={4}>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={prevPage}
                  isDisabled={currentPage === 1}
                  aria-label="Previous Page"
                />
                <Text fontWeight="bold">
                  Page {currentPage} of {Math.ceil(filteredListings.length / listingsPerPage)}
                </Text>
                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={nextPage}
                  isDisabled={currentPage === Math.ceil(filteredListings.length / listingsPerPage)}
                  aria-label="Next Page"
                />
              </Flex>
            </>
          )}
        </Container>
      </Box>

      {/* Listing Details Modal */}
      <ListingDetailsModal isOpen={isOpen} onClose={closeListingDetails} listing={selectedListing} />
    </Box>
  );
};

export default ListingPage;
